import { models } from "@/components/toLoad/models";

import _axios from "axios";
import _moment from "moment";
import { capture, captureException } from "@/posthog";
import { apiUser } from "@/components/toLoad/api";
import packageInfo from "../../../package.json";

export const katana = {
  /**
   * ? ROLES
   */
  /**
   * Change the position of the role of a user in a company
   * @param {String} user
   * @param {String} company
   * @param {Number} role
   */
  statusRole(user, company, role) {
    return apiUser({
      method: "patch",
      url: `/companies/${company}/members/${user}`,
      data: { position: role },
    });
  },

  /**
   * ? USERS
   */
  async createUser(userObject, password) {
    const user = { ...userObject };
    delete user.password;
    try {
      const { data } = await apiUser({
        method: "post",
        url: "/admin/users",
        data: { email: userObject.email, password, user },
      });
      capture("user_signed_up", { source: "admin" });
      return data;
    } catch (error) {
      captureException(error, { flow: "admin_create_user" });
      throw error;
    }
  },
  /**
   * Remove a user from a company
   * @param {String} user
   * @param {String} company
   */
  removeUserFromCompany(user, company) {
    return apiUser({
      method: "delete",
      url: `/companies/${company}/members/${user}`,
    });
  },
  /**
   * Add a user from a company
   * @param {String} user
   * @param {String} company
   */
  addUserToCompany(user, company, role = 1) {
    return apiUser({
      method: "post",
      url: `/companies/${company}/members`,
      data: { userId: user, position: role },
    });
  },

  /**
   * ? CUSTOMERS
   */
  async getCustomer(companyId, customerId, privacy = true) {
    try {
      const { data } = await apiUser({ url: `/companies/${companyId}/customers/${customerId}` });
      if (privacy)
        return {
          avatar: data.avatar,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          company: data.company,
          id: data.id,
        };
      return data;
    } catch {
      return {
        ...models.user,
        firstName: "Inconnu",
        lastName: "Inconnu",
        id: 0,
      };
    }
  },

  /**
   * ? COMPANIES
   */
  async getCompany(companyDoc, privacy = true) {
    const companyId = String(companyDoc).split("/").findLast(Boolean);
    try {
      const { data } = await apiUser({ url: `/companies/${companyId}` });
      if (privacy)
        return {
          avatar: data.avatar,
          name: data.name,
          email: data.email,
          website: data.website,
          id: data.id,
        };
      return data;
    } catch {
      return {
        avatar: "",
        name: "Inconnu",
        email: "",
        website: "",
        id: 0,
      };
    }
  },
  /**
   * Overwrite form fields only with non-empty looked-up values (keeps what the user typed)
   * @param {Object} form Current form values
   * @param {Object} found Result of getCompanyBySIRET
   * @returns {Object}
   */
  mergeSIRETResult(form, found) {
    const filled = (value) => value !== null && value !== undefined && value !== "";
    const pick = (source, keys) => Object.fromEntries(keys.filter((key) => filled(source?.[key])).map((key) => [key, source[key]]));
    return {
      ...form,
      ...pick(found, ["siret", "birthdate", "name", "type"]),
      address: { ...form.address, ...pick(found.address, ["street", "postalCode", "city", "country", "region"]) },
    };
  },
  /**
   * Retrieve informations from SIRENE API for a specified company
   * @param {String} siret SIRET de l'entreprise à vérifier
   * @param {Object} base Entreprise à compléter avec les données SIRENE
   * @returns {Promise}
   */
  getCompanyBySIRET(siret) {
    const companyEntry = JSON.parse(JSON.stringify(models.company));

    const returnPromise = new Promise((resolve, reject) => {
      apiUser({
        method: "get",
        url: `/integrations/insee/${siret}`,
      }).then((result) => {
        const companyResponse = result.data?.etablissement;
        if (!companyResponse) {
          reject(new Error("Établissement introuvable pour ce SIRET."));
          return;
        }

        companyEntry.siret = companyResponse.siret;

        const unite = companyResponse.uniteLegale || {};
        if (unite.dateCreationUniteLegale)
          companyEntry.birthdate = _moment(unite.dateCreationUniteLegale).format("YYYY-MM-DD");

        if (unite.denominationUniteLegale)
          companyEntry.name = unite.denominationUniteLegale;
        else {
          const gender = unite.sexeUniteLegale === "F" ? "Mme" : "M";
          companyEntry.name = `${gender} ${unite.prenom1UniteLegale || ""} ${unite.nomUniteLegale || ""}`.trim();
        }

        companyEntry.type = unite.categorieEntreprise;

        const addr = companyResponse.adresseEtablissement || {};
        const streetParts = [
          addr.numeroVoieEtablissement,
          addr.typeVoieEtablissement,
          addr.libelleVoieEtablissement,
        ].filter((part) => part && part !== "[ND]");

        companyEntry.address.street = streetParts.join(" ");
        companyEntry.address.postalCode = addr.codePostalEtablissement || "";
        companyEntry.address.city = addr.libelleCommuneEtablissement || "";
        companyEntry.address.country = "France";
        companyEntry.address.region = "";

        const hiddenAddress = addr.numeroVoieEtablissement === "[ND]" || !streetParts.length;
        if (hiddenAddress) {
          resolve(companyEntry);
          return;
        }

        const query = encodeURIComponent(streetParts.join(" "));
        const postcode = encodeURIComponent(addr.codePostalEtablissement || "");

        _axios({
          method: "get",
          url: `https://api-adresse.data.gouv.fr/search/?q=${query}&postcode=${postcode}&limit=1`,
          headers: {
            Accept: "application/json",
          },
        }).then((response) => {
          const addressResponse = response.data?.features?.[0]?.properties;
          if (addressResponse) {
            companyEntry.address.street = addressResponse.name || companyEntry.address.street;
            companyEntry.address.postalCode = addressResponse.postcode || companyEntry.address.postalCode;
            companyEntry.address.city = addressResponse.city || companyEntry.address.city;
            companyEntry.address.country = "France";
            const splitContext = String(addressResponse.context || "").split(", ");
            if (splitContext[1]) companyEntry.address.region = splitContext[1];
          }
          resolve(companyEntry);
        }).catch((err) => {
          console.log(err);
          resolve(companyEntry);
        });
      }).catch((err) => {
        reject(new Error(err?.response?.data?.message || err.message || "Recherche SIRET impossible."));
      });
    });

    return returnPromise;
  },
  async getCompanyUsers(companyId) {
    const { data } = await apiUser({ url: `/companies/${companyId}/members` });
    return (data.documents || []).map((member) => ({
      ...member,
      userData: member.user,
    }));
  },

  /**
   * ? DATABASE
   */
  async getUser(userID, privacy = true) {
    try {
      const { data } = await apiUser({ url: `/users/${userID}` });
      if (!privacy) return data;
      return {
        avatar: data.avatar,
        civility: data.civility,
        firstName: data.firstName,
        lastName: data.lastName,
        computeName: data.computeName,
        email: data.email,
        company: data.company,
        id: data.id,
      };
    } catch {
      return {
        avatar: "",
        civility: "",
        firstName: "Inconnu",
        lastName: "",
        computeName: "",
        email: "",
        id: 0,
      };
    }
  },

  /**
   * ? LOGISTICS
   */
  /**
   * Retrieve all warehouses from a company
   * @param {String} companyId
   * @returns
   */
  async getWarehouses(companyId) {
    const { data } = await apiUser({ url: `/companies/${companyId}/warehouses` });
    return data.documents || [];
  },
  async getWarehouse(companyId, warehouseId) {
    const { data } = await apiUser({ url: `/companies/${companyId}/warehouses/${warehouseId}` });
    const products = await apiUser({ url: `/companies/${companyId}/warehouses/${warehouseId}/products` });
    const authorData = await this.getUser(data.author);
    return {
      ...data,
      products: (products.data.documents || []).length,
      authorData,
    };
  },
  async getWarehouseProducts(companyId, warehouseId, getReference = false, combineReference = false) {
    const listed = await apiUser({
      method: "get",
      url: `/companies/${companyId}/warehouses/${warehouseId}/products`,
    });

    const normalizePictures = (pictures) => {
      if (!Array.isArray(pictures)) return pictures;
      return pictures.map((picture, key) => {
        if (typeof picture === "string") return { key, link: picture };
        return picture;
      });
    };

    const productsPromise = (listed.data.documents || []).map(async (product) => {
      let productOutput = { ...product };

      if (getReference)
        try {
          const productData = (await apiUser({
            method: "get",
            url: `/companies/${companyId}/products/${product.id}`,
          })).data.product;

          const normalized = {
            ...productData,
            pictures: normalizePictures(productData.pictures),
          };

          if (combineReference) productOutput = {
            ...productOutput,
            ...normalized,
            id: product.id,
          };
          else productOutput.reference = normalized;
        } catch {
          if (!combineReference) productOutput.reference = null;
        }


      return productOutput;
    });

    return Promise.all(productsPromise);
  },

  /**
   * ? COMPANY TOKENS
   */
  async generateCompanyToken(userInfo, tokenName, mode = "r") {
    try {
      const result = await apiUser({
        method: "post",
        url: `/companies/${userInfo.company}/tokens`,
        data: { tokenName, mode, katanaVersion: packageInfo.version },
      });

      return result.data.token;
    } catch (err) {
      console.error(err);
      throw new Error("Unable to generate company token", { cause: err });
    }
  },

  async statusCompanyToken(company, token, isActive) {
    try {
      await apiUser({
        method: "patch",
        url: `/companies/${company}/tokens/${encodeURIComponent(token.id)}`,
        data: { active: isActive },
      });
      return true;
    } catch (err) {
      throw new Error(`❌ Unable to modify status company token : ${err}`, { cause: err });
    }
  },

  async revokeCompanyToken(token, company) {
    try {
      await apiUser({
        method: "delete",
        url: `/companies/${company}/tokens/${encodeURIComponent(token.id)}`,
      });
      return true;
    } catch (err) {
      throw new Error(`❌ Unable to modify status company token : ${err}`, { cause: err });
    }
  },

  async findCompanyTokenByName(name, company) {
    try {
      const { data } = await apiUser({ url: `/companies/${company}/tokens` });
      return (data.documents || []).find((token) => token.name === name);
    } catch (err) {
      throw new Error(`❌ Unable to find company token by name : ${err}`, { cause: err });
    }
  },
};
